import toCurrency from '@orn/shared-web/utils/money/toCurrency';

import AmountRangeBar, { AMOUNT_RANGE_BAR_HEIGHT_PX } from '../../charts/AmountRangeBar';
import RecordPane from '../../entity/RecordPane';

import type { TransactionStats } from '../../../hooks/queries/useTransactionStats';

export interface IMerchantSpendingSectionBaseProps {
  /** All-time debit stats at the merchant. `null` renders nothing. */
  stats: TransactionStats | null;
  /** Draws the skeleton in place of the card body while `stats` is still null. */
  loading: boolean;
  /** This record's amount, the bar's current marker. `null` drops the bar and keeps the rows. */
  currentAmount: number | null;
  /** Whether this record is one of the debits `stats` counts. When it is the only one, nothing else is there to compare against and the section hides. */
  currentInStats: boolean;
  /** Where "View merchant" goes. */
  viewMerchantPath: string;
  className?: string;
}

/**
 * The "Spending" section of a record page (Figma "Transaction/Card/Merchant
 * Summary"): how this record's amount sits against everything the household
 * has spent at the merchant. An `AmountRangeBar` over the merchant's all-time
 * min / average / max, then "Total transactions" and "Total spending" rows.
 * A merchant whose only counted debit is this record has nothing to compare it
 * against, so the section hides. Pure; `MerchantSpendingSection` feeds it from
 * `getTransactionStats`.
 */
const MerchantSpendingSectionBase = ({
  stats,
  loading,
  currentAmount,
  currentInStats,
  viewMerchantPath,
  className,
}: IMerchantSpendingSectionBaseProps) => {
  const otherDebitCount = stats ? stats.count - (currentInStats ? 1 : 0) : 0;
  const comparableStats = stats && otherDebitCount > 0 ? stats : null;

  if (!comparableStats && !loading) {
    return null;
  }

  return (
    <RecordPane.Section title="Spending" link={{ label: 'View merchant', to: viewMerchantPath }} className={className}>
      {comparableStats ? <SpendingBody stats={comparableStats} currentAmount={currentAmount} /> : <SpendingSkeleton />}
    </RecordPane.Section>
  );
};

interface ISpendingBodyProps {
  stats: TransactionStats;
  currentAmount: number | null;
}

const SpendingBody = ({ stats, currentAmount }: ISpendingBodyProps) => {
  return (
    <>
      {currentAmount !== null && (
        <div className="px-5 py-8">
          <AmountRangeBar
            min={Number(stats.min)}
            max={Number(stats.max)}
            average={Number(stats.average)}
            current={currentAmount}
            currencyCode={stats.currencyCode}
          />
        </div>
      )}
      <RecordPane.Row label="Total transactions">{stats.count.toLocaleString()}</RecordPane.Row>
      <RecordPane.Row label="Total spending">
        {toCurrency(stats.total, { currencyCode: stats.currencyCode })}
      </RecordPane.Row>
    </>
  );
};

const SpendingSkeleton = () => {
  return (
    <>
      <div className="px-5 py-8" aria-busy="true" aria-label="Loading spending">
        <div className="bg-tertiary animate-pulse rounded-md" style={{ height: AMOUNT_RANGE_BAR_HEIGHT_PX }} />
      </div>
      <SkeletonRow labelWidthClassName="w-36" />
      <SkeletonRow labelWidthClassName="w-28" />
    </>
  );
};

interface ISkeletonRowProps {
  labelWidthClassName: string;
}

const SkeletonRow = ({ labelWidthClassName }: ISkeletonRowProps) => {
  return (
    <div className="flex h-16 items-center justify-between px-4" aria-hidden="true">
      <div className={`bg-tertiary h-4 animate-pulse rounded-md ${labelWidthClassName}`} />
      <div className="bg-tertiary h-4 w-16 animate-pulse rounded-md" />
    </div>
  );
};

export default MerchantSpendingSectionBase;
